// AGE-GAP(anchored-overlay) · vendor/age-design/SOURCE_COMMIT 7322a157 · spec 002
// contracts/shared-fixes.md §2 · data-model.md §3
//
// The one anchored-surface primitive for every portalled overlay in SIMSM. AGE
// ships no menu, dropdown, popover or positioning primitive — 46 vendored
// components, none of them anchored — so placement is a SIMSM composite over AGE
// primitives rather than a workaround for a broken AGE component. The marker
// above names the surveyed vendor commit so this can be retired if AGE ever
// ships a positioning primitive. It declares no design token; R2 is not engaged.
//
// Consumers supply the trigger and the content and never compute their own
// `position: fixed` offsets. A consumer that does is the defect returning: the
// idiom this replaces put the last aggregates row's menu at top 890 of a 900px
// viewport, running to 1095 — 195px below the fold, unreachable because the page
// could scroll only 132px and scrolling dismissed the menu anyway.
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

export type AnchoredPlacement = 'below' | 'above';

/** Which trigger edge the overlay lines up with horizontally. */
export type AnchoredAlign = 'start' | 'end';

export interface AnchoredRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface AnchoredSize {
  width: number;
  height: number;
}

// Every option is explicitly `| undefined`: the workspace runs with
// exactOptionalPropertyTypes, and the hook forwards its own unset options
// straight through, which is passing `undefined` rather than omitting the key.
export interface AnchoredOverlayOptions {
  /** `start` aligns the left edges, `end` the right ones. Default `start`. */
  align?: AnchoredAlign | undefined;
  /** Gap between trigger and overlay. Default 4 — the offset the menus had. */
  offset?: number | undefined;
  /** Smallest gap the overlay keeps from every viewport edge. Default 8. */
  margin?: number | undefined;
  /**
   * Floor for the height cap. Without it a trigger sitting on the fold would be
   * "placed" in the 2px below it and still show nothing; the vertical clamp then
   * pulls that height back inside the viewport.
   */
  minHeight?: number | undefined;
  /** Give the overlay the trigger's width — what a combobox panel wants. */
  matchTriggerWidth?: boolean | undefined;
}

export interface AnchoredGeometry extends AnchoredOverlayOptions {
  /** Trigger box in viewport coordinates, i.e. `getBoundingClientRect()`. */
  trigger: AnchoredRect;
  /**
   * The box the overlay wants: its content under its own stylesheet, before any
   * cap of the helper's.
   */
  overlay: AnchoredSize;
  viewport: AnchoredSize;
}

export interface AnchoredPosition {
  top: number;
  left: number;
  /** The tallest this spot allows. Only worth applying when `heightCapped`. */
  maxHeight: number;
  /** The widest this spot allows. Only worth applying when `widthCapped`. */
  maxWidth: number;
  /**
   * The content is taller than the room, so the viewport genuinely requires the
   * cap. When it is false the overlay already fits and the helper must write no
   * `max-height` at all: an inline value outranks the consumer's own class rule,
   * which is how `.simsm-medicombo__panel` and `.simsm-medmulti__panel` silently
   * lost their `max-height: 320px` (they rendered 330px and 344px, uncapped and
   * unscrollable, under an inline cap of 668px and 601px).
   */
  heightCapped: boolean;
  /** Same rule for the width: only written when the content overflows it. */
  widthCapped: boolean;
  /** Only set when `matchTriggerWidth` is on; otherwise the content decides. */
  width?: number;
  placement: AnchoredPlacement;
}

export interface UseAnchoredOverlayInput extends AnchoredOverlayOptions {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  overlayRef: RefObject<HTMLElement | null>;
}

export interface UseAnchoredOverlayResult {
  /** Spread onto the portalled overlay element. Positions and caps it. */
  style: CSSProperties;
  /** `null` until the first measurement lands; useful as `data-placement`. */
  placement: AnchoredPlacement | null;
}

const DEFAULT_OFFSET = 4;
const DEFAULT_MARGIN = 8;
const DEFAULT_MIN_HEIGHT = 96;

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * Where an overlay of this size goes next to this trigger. Pure — it takes
 * measurements and returns coordinates, so flip, clamp and cap are each
 * testable on their own. Flip alone rescues a trigger near the fold; clamp alone
 * rescues content taller than the viewport; neither alone is enough.
 *
 * `overlay.height` is what the surface wants *after* its own stylesheet has had
 * its say, not the raw content height — a consumer capped at 320px asks for 320,
 * so it is placed as a 320px box and told (`heightCapped: false`) that the helper
 * has nothing to add.
 */
export function computeAnchoredPosition(geometry: AnchoredGeometry): AnchoredPosition {
  const { trigger, overlay, viewport } = geometry;
  const offset = geometry.offset ?? DEFAULT_OFFSET;
  const margin = geometry.margin ?? DEFAULT_MARGIN;
  const minHeight = geometry.minHeight ?? DEFAULT_MIN_HEIGHT;

  const maxWidth = Math.max(0, viewport.width - margin * 2);
  const heightBudget = Math.max(0, viewport.height - margin * 2);

  const roomBelow = viewport.height - margin - (trigger.bottom + offset);
  const roomAbove = trigger.top - offset - margin;
  const placement: AnchoredPlacement =
    overlay.height <= roomBelow
      ? 'below'
      : overlay.height <= roomAbove
        ? 'above'
        : // Neither side fits: take the roomier one and let the cap scroll.
          roomAbove > roomBelow
          ? 'above'
          : 'below';

  const room = placement === 'above' ? roomAbove : roomBelow;
  const maxHeight = clamp(room, Math.min(minHeight, heightBudget), heightBudget);
  const height = Math.min(overlay.height, maxHeight);
  const top = clamp(
    placement === 'above' ? trigger.top - offset - height : trigger.bottom + offset,
    margin,
    Math.max(margin, viewport.height - margin - height),
  );

  const width = Math.min(
    geometry.matchTriggerWidth === true ? trigger.right - trigger.left : overlay.width,
    maxWidth,
  );
  // Clamping `left` also protects the width: a fixed box with no width shrinks to
  // the space right of `left`, so an unclamped `left` would wrap the content.
  const left = clamp(
    geometry.align === 'end' ? trigger.right - width : trigger.left,
    margin,
    Math.max(margin, viewport.width - margin - width),
  );

  return {
    top,
    left,
    maxHeight,
    maxWidth,
    // Only a surface that does not fit needs the helper's cap. Reporting this
    // separately from `maxHeight` keeps the geometry answerable ("the tallest
    // this spot allows") while letting the caller stay out of the way when the
    // answer is not binding.
    heightCapped: overlay.height > maxHeight,
    widthCapped: overlay.width > maxWidth,
    placement,
    ...(geometry.matchTriggerWidth === true ? { width } : {}),
  };
}

function samePosition(a: AnchoredPosition, b: AnchoredPosition): boolean {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.maxHeight === b.maxHeight &&
    a.maxWidth === b.maxWidth &&
    a.heightCapped === b.heightCapped &&
    a.widthCapped === b.widthCapped &&
    a.width === b.width &&
    a.placement === b.placement
  );
}

/**
 * What the content wants, read through the cap the overlay is already wearing:
 * the rendered box plus whatever the cap is currently hiding. Measuring this way
 * lets a reposition re-measure without first tearing the cap off and flashing the
 * full-height surface. The rendered box comes from `getBoundingClientRect()`
 * rather than `offsetWidth`, which rounds to whole pixels and would drift the
 * right-aligned edge off its trigger by a fraction.
 */
function naturalSize(element: HTMLElement): AnchoredSize {
  const rect = element.getBoundingClientRect();
  return {
    width: rect.width + Math.max(0, element.scrollWidth - element.clientWidth),
    height: rect.height + Math.max(0, element.scrollHeight - element.clientHeight),
  };
}

/**
 * The `max-height` the consumer's own stylesheet puts on the surface, in px, or
 * `Infinity` for none. Read so the helper can leave a consumer that already caps
 * itself alone instead of overwriting it — an inline `max-height` beats a class
 * rule no matter how specific the rule is, so writing one unconditionally
 * repeals every consumer's own cap.
 *
 * Only `px` is honoured. Percentages resolve against the containing block and
 * `getComputedStyle` hands them back unresolved; treating one as "no cap" means
 * the helper's own viewport cap still applies, which keeps the surface on screen
 * — the failure mode that matters. `vh`, `rem` and `calc()` over absolute units
 * all arrive already resolved to px.
 */
function ownMaxHeight(element: HTMLElement): number {
  const declared = getComputedStyle(element).maxHeight;
  if (!declared.endsWith('px')) return Number.POSITIVE_INFINITY;
  const px = Number.parseFloat(declared);
  return Number.isFinite(px) ? px : Number.POSITIVE_INFINITY;
}

/**
 * Keeps a portalled overlay anchored to its trigger and fully on screen for as
 * long as it is open. Scrolling REPOSITIONS it — dismissing on scroll is the
 * bug, because an overlay below the fold can then never be reached.
 *
 * It writes the least it can: `max-height` and its scroll appear only when the
 * viewport actually requires them. Every inline declaration here outranks the
 * consumer's own class rule, so anything written unconditionally is a silent
 * repeal of that rule.
 */
export function useAnchoredOverlay(input: UseAnchoredOverlayInput): UseAnchoredOverlayResult {
  const { open, triggerRef, overlayRef, align, offset, margin, minHeight, matchTriggerWidth } =
    input;
  const [position, setPosition] = useState<AnchoredPosition | null>(null);
  // The consumer's own cap, and whether the element is currently wearing ours.
  // Once ours is on the element `getComputedStyle` can no longer see past it to
  // theirs, so it is read on the passes where we are provably applying none —
  // the first one always is — and remembered for the passes where we are.
  const ownCap = useRef(Number.POSITIVE_INFINITY);
  const applyingCap = useRef(false);

  const reposition = useCallback((): void => {
    const trigger = triggerRef.current;
    const overlay = overlayRef.current;
    if (!trigger || !overlay) return;
    const rect = trigger.getBoundingClientRect();
    if (!applyingCap.current) ownCap.current = ownMaxHeight(overlay);
    const content = naturalSize(overlay);
    const next = computeAnchoredPosition({
      trigger: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
      // Placed as the box the consumer's own stylesheet makes it, not as the raw
      // content: a panel that caps itself at 320px occupies 320px, and a flip
      // decision taken on the uncapped 800px would be a decision about a surface
      // that never renders.
      overlay: { width: content.width, height: Math.min(content.height, ownCap.current) },
      // clientWidth/clientHeight, not innerWidth/innerHeight: a fixed offset is
      // relative to the layout viewport, which excludes a persistent scrollbar.
      viewport: {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      },
      align,
      offset,
      margin,
      minHeight,
      matchTriggerWidth,
    });
    // What the next render puts on the element, so the pass after it knows
    // whether the cap it can see is the consumer's or ours.
    applyingCap.current = next.heightCapped;
    // Same numbers, same object: a ResizeObserver that fires on the cap this very
    // call applied must not start a render loop.
    setPosition((previous) =>
      previous !== null && samePosition(previous, next) ? previous : next,
    );
  }, [triggerRef, overlayRef, align, offset, margin, minHeight, matchTriggerWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      // Only here, never in the cleanup: the cleanup also runs on a re-render
      // that keeps the overlay open, and forgetting mid-flight that our cap is
      // on the element would read it back as the consumer's and ratchet.
      ownCap.current = Number.POSITIVE_INFINITY;
      applyingCap.current = false;
      return undefined;
    }
    // Runs before paint, so the hidden measuring pass is never shown.
    reposition();

    let frame = 0;
    function schedule(): void {
      if (frame !== 0) return;
      // One measurement per frame: a scroll fires far more often than that.
      frame = requestAnimationFrame(() => {
        frame = 0;
        reposition();
      });
    }
    // Capture phase: the trigger may live inside a scrolling container whose
    // scroll event never reaches window on the bubble path.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    // Content can change while open (a combobox list narrows as the user types),
    // and that changes where the surface belongs.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    if (observer) {
      if (overlayRef.current) observer.observe(overlayRef.current);
      if (triggerRef.current) observer.observe(triggerRef.current);
    }
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [open, reposition, overlayRef, triggerRef]);

  if (!open || position === null) {
    // The measuring pass: laid out at full size so `scrollHeight` is honest, and
    // transparent rather than `visibility: hidden` — a hidden element cannot take
    // focus, and a menu moves focus into itself the moment it opens. The layout
    // effect above replaces this before the browser paints.
    return {
      style: { position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' },
      placement: null,
    };
  }

  return {
    style: {
      position: 'fixed',
      top: position.top,
      left: position.left,
      width: position.width,
      // Written only when the surface really does not fit, because inline beats
      // the consumer's class rule and an unconditional cap repeals it. When the
      // cap is applied the scroll comes with it: the cap belongs to the overlay,
      // and the page's scroll cannot reach a fixed box.
      ...(position.heightCapped ? { maxHeight: position.maxHeight, overflowY: 'auto' } : {}),
      ...(position.widthCapped ? { maxWidth: position.maxWidth } : {}),
    },
    placement: position.placement,
  };
}
